import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import { AppContext } from "../../context/AppProvider";
import { AuthContext } from "../../context/AuthProvider";
import { fetchWithInterceptor } from "../../utils/fetchWithInterceptor";

const DropArea = styled('div')(({ theme }) => ({
  border: '2px dashed #aaa',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
}));

const CustomDropZone = ({ label, file, setFile }) => {
  const handleChangeFile = (files) => {
    setFile(files[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => handleChangeFile(acceptedFiles),
    maxFiles: 1,
    maxSize: 10000000, // 10MB
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    }
  });

  return (
    <DropArea {...getRootProps()}>
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
      <Typography  sx={{ textAlign: 'center', fontSize: '1.5rem' }}>
        {label}
      </Typography>
    </DropArea>
  );
}

export default function UploadCostCsvDialog({ open, onClose }) {
  const [liquorConnectFile, setLiquorConnectFile] = useState(null);
  const [cin7File, setCin7File] = useState(null);
  const [loading, setLoading] = useState(false);

  const context = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const formData  = new FormData();
      formData.append('liquorconnect', liquorConnectFile);
      formData.append('cin7', cin7File);
  
      const response = await fetchWithInterceptor('/api/integrations/compare-costs', {
        method: 'POST',
        body: formData,
        headers: {
          'x-access-token': authContext.getToken()
        },
      });
      setLoading(false);
      if (response.ok) {
        onClose(true);
        const data = await response.json();
        context.showMessage(data?.message || "Files uploaded successfully", "success");
      } else {
        const error = await response.json();
        context.showMessage(error?.message || "Invalid file", "error");
      }
    } catch (error) {
      console.log(error);
      context.showMessage("Invalid file", "error");
      setLoading(false);
    }
  };

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth="md"
      aria-labelledby="delete-dialog"
      open={open}
    >
      <DialogTitle id="delete-dialog">Upload Cost CSVs</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 6 }}>
            <Box sx={{ minHeight: 200 }}>
              <CustomDropZone label="Drag and drop the Liquor Connect file here or click" file={liquorConnectFile} setFile={setLiquorConnectFile} />
            </Box>
            <br />
            <Typography variant="subtitle1">
              Upload a CSV file separated by comma ","
            </Typography>
            <Typography variant="subtitle1">
              {`Uploaded file: ${liquorConnectFile ? liquorConnectFile.name : '-'}`}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Box sx={{ minHeight: 200 }}>
              <CustomDropZone label="Drag and drop the Cin7 file here or click" file={cin7File} setFile={setCin7File} />
            </Box>
            <br />
            <Typography variant="subtitle1">
              Upload a CSV file separated by comma ","
            </Typography>
            <Typography variant="subtitle1">
              {`Uploaded file: ${cin7File ? cin7File.name : '-'}`}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading} sx={{ width: 100, height: 36.5 }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
